import { supabase } from '../supabaseClient';
import { purgeUserScopedCaches } from './userScopedCache';
import { clearSupabaseAuthStorage } from './authSession';

export { clearSupabaseAuthStorage } from './authSession';

const CLEANUP_TIMEOUT_MS = 2000;
let pendingSignOut = null;

function browserIdentity() {
  try {
    return {
      key: sessionStorage.getItem('ss_session_key'),
      owner: sessionStorage.getItem('ss_session_user_id'),
    };
  } catch {
    return { key: null, owner: null };
  }
}

async function removeOwnedSession({ key, owner }) {
  // Never guess the owner of a legacy key; RLS is an additional guard.
  if (!key || !owner) return;
  const controller = new AbortController();
  let timer;
  try {
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Session cleanup timed out'));
      }, CLEANUP_TIMEOUT_MS);
    });
    const { error } = await Promise.race([
      supabase.from('user_sessions').delete()
        .eq('user_id', owner).eq('session_id', key)
        .abortSignal(controller.signal),
      timeout,
    ]);
    if (error) console.warn('[SiteScope] Device-session cleanup failed; continuing sign-out.');
  } catch {
    console.warn('[SiteScope] Device-session cleanup unavailable; continuing sign-out.');
  } finally {
    clearTimeout(timer);
  }
}

async function clearLocalSession() {
  try { sessionStorage.clear(); } catch { /* ignore */ }

  try {
    [
      'ss_pa_key',
      'ss_share_redirect',
      'ss_pending_deeplink',
      'ss_signup_from_share',
    ].forEach(key => localStorage.removeItem(key));
  } catch {
    /* ignore storage access failures */
  }

  // Remove every account-scoped cache so the next user on this browser never
  // hydrates from the signed-out user's cached pipeline/zones/pro data (WP-19).
  purgeUserScopedCaches();

  clearSupabaseAuthStorage();

  if ('caches' in window) {
    try {
      const names = await caches.keys();
      await Promise.all(
        names.filter(name => name.startsWith('sitescope')).map(name => caches.delete(name))
      );
    } catch {
      /* ignore cache cleanup failures */
    }
  }
}

// Keep credentials until the authenticated row deletion finishes. Cleanup is
// bounded and best-effort: a failure must never prevent the requested sign-out.
// The caller guard also covers an auth event that arrives before a new account's
// browser owner/key has been prepared by deferred validation.
export function signOutAndClearSiteScopeSession({ isCurrent = () => true } = {}) {
  if (pendingSignOut) return pendingSignOut;
  const identity = browserIdentity();
  const ownsCleanup = () => {
    const current = browserIdentity();
    return isCurrent() && current.key === identity.key && current.owner === identity.owner;
  };
  pendingSignOut = (async () => {
    await removeOwnedSession(identity);
    if (!ownsCleanup()) return;
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) console.warn('[SiteScope] Supabase sign-out returned an error; clearing local session.');
    } catch {
      console.warn('[SiteScope] Supabase sign-out failed; clearing local session anyway.');
    } finally {
      if (ownsCleanup()) await clearLocalSession();
    }
  })().finally(() => { pendingSignOut = null; });
  return pendingSignOut;
}
